import {
  DialogForm,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  SelectType,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
  useDialogExitWarning,
} from '@ais-ui/ui-core';
import {
  Filter,
  FilterMutation,
  useGetShareGroups,
  useSession,
  validateFilter,
} from '@ais-ui/websockets';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { GroupMultiSelect } from '../../../Groups/GroupMultiSelect';
import { grabFilterInitialGroups } from '../../../Groups/grabInitialGroups';
import React, { useEffect, useMemo, useState } from 'react';
import { mutateCallbacks } from '../../../utils/mutateCallbacks';
import { useLocalization } from '@ais-ui/locale-management';
import { AutoCompleteFilterTextarea } from '../../QueryBuilder/AutoCompleteFilterTextarea';
import { useOwnershipCheck } from '../../../utils/useOwnershipCheck';
import { FilterBuilder } from '../../QueryBuilder/FilterBuilder';
import { isComplexFilter, isFilterApplied } from '../../utils';
import { useFilterContext } from '../../../Map/FilterContext';
import { FilterCriteria } from '../../constants';
import { validateFilterSyntax } from '../../QueryBuilder/utils/utils';

interface SaveFilterDialogProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  mutate: FilterMutation;
  error: Error | null;
  action: 'create' | 'edit';
  filter?: Filter;
}

interface FilterFormData {
  name: string;
  definition: string;
  selectedGroups: SelectType[];
}
interface SaveFilterFormProps {
  form: UseFormReturn<FilterFormData>;
  isOwner: boolean;
}

const createDefinitionSchema = (
  filterOptions: Record<string, FilterCriteria>
) => {
  return z.string().superRefine((value, ctx) => {
    const { isValid, errorMessage } = validateFilterSyntax(
      value,
      filterOptions
    );
    if (!isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          errorMessage ||
          'Ensure that you have a field, operator, and a value.',
      });
    }
  });
};

// Create a schema factory function that creates the entire form schema
const createFormSchema = (filterOptions: Record<string, FilterCriteria>) => {
  return z.object({
    name: z.string().nonempty('Name is required'),
    definition: createDefinitionSchema(filterOptions),
    selectedGroups: z.array(
      z.object({
        value: z.string(),
        label: z.string(),
      })
    ),
  });
};

const SaveFilterForm = ({ form, isOwner }: SaveFilterFormProps) => {
  const { messages } = useLocalization();
  const definition = form.watch('definition');
  const { filterOptions } = useFilterContext();

  const [tab, setTab] = useState(() => {
    return isComplexFilter(definition) ? 'autocomplete' : 'builder';
  });

  useEffect(() => {
    if (isComplexFilter(definition) && tab === 'builder') {
      setTab('autocomplete');
    }
  }, [definition, filterOptions, tab]);

  return (
    <>
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.filtersCreateNameLabel} *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Input id="filter-name" type="text" autoFocus {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="space-y-4">
        <div className="flex justify-between h-full">
          <Tabs className="w-[286px]" value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2">
              {isComplexFilter(definition) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="inline-flex items-center justify-center whitespace-nowrap">
                      <TabsTrigger
                        id="filter-simple-tab"
                        disabled={true}
                        value="builder"
                      >
                        {messages.filtersCreateSimpleTabLabel}
                      </TabsTrigger>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {messages.filtersCreateTooltipSimple}
                  </TooltipContent>
                </Tooltip>
              )}
              {!isComplexFilter(definition) && (
                <TabsTrigger value="builder">
                  {messages.filtersCreateSimpleTabLabel}
                </TabsTrigger>
              )}
              <TabsTrigger id="filter-advanced-tab" value="autocomplete">
                {messages.filtersCreateAdvancedTabLabel}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {tab === 'autocomplete' && (
            <p className="text-xs text-muted-foreground mt-4 h-full">
              Press{' '}
              <kbd className="px-1 py-0.5 bg-muted rounded border">Ctrl</kbd>+
              <kbd className="px-1 py-0.5 bg-muted rounded border">Space</kbd>{' '}
              to open suggestions
            </p>
          )}
        </div>
        {tab === 'builder' && (
          <FormField
            control={form.control}
            name="definition"
            render={({ field }) => (
              <FormItem>
                <FormControl onChange={() => form.clearErrors()}>
                  <FilterBuilder
                    id="filter-builder-definition"
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {tab === 'autocomplete' && (
          <FormField
            control={form.control}
            name="definition"
            render={({ field }) => (
              <FormItem>
                <FormControl onChange={() => form.clearErrors()}>
                  <AutoCompleteFilterTextarea
                    id="filter-autocomplete-definition"
                    placeholder="IMO = 9629495"
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
      <FormField
        control={form.control}
        name="selectedGroups"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.filtersCreateShareWithLabel}</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <GroupMultiSelect
                value={field.value}
                onChange={field.onChange}
                disabled={!isOwner}
                disabledReason={
                  messages.genericNotOwnerDisabledText ||
                  'You must be the owner to share this object'
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};

export function SaveFilterDialog(props: SaveFilterDialogProps) {
  const { messages } = useLocalization();
  const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { olSession } = useSession();
  const { customFilters, setCustomFilters } = useFilterContext();
  const { isOwner } = useOwnershipCheck();

  const { filterOptions } = useFilterContext();

  // Create a memoized schema that depends on filterOptions
  const formSchema = useMemo(() => {
    return createFormSchema(filterOptions);
  }, [filterOptions]);

  const form = useForm<FilterFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      selectedGroups: [],
      name: props.filter?.name || '',
      definition: props.filter?.filter || '',
    },
  });

  useEffect(() => {
    // Check if groups have loaded
    if (!isLoadingGroups) {
      const defaultValues = {
        name: props.filter?.name || '',
        definition: props.filter?.filter || '',
        selectedGroups: grabFilterInitialGroups(props.filter, groups),
      };
      form.reset(defaultValues);
    }
  }, [props.filter, groups, isLoadingGroups, form]);

  const { toast } = useToast();

  const [dialogTitle, dialogDescription] =
    props.action === 'edit'
      ? [messages.filtersEditTitle, messages.filtersEditDescription]
      : [messages.filtersCreateTitle, messages.filtersCreateDescription];

  const onSubmit = async (data: FilterFormData) => {
    const validateResult = await validateFilter(olSession, data.definition);

    setIsSubmitting(true);

    if (!validateResult.isValid) {
      form.setError('definition', {
        type: 'manual',
        message: `Invalid filter: ${validateResult.errorMessage}`,
      });
      setIsSubmitting(false);
      return;
    }

    const filterToSave: Filter = {
      name: data.name,
      filter: data.definition,
      isEditable: props.filter?.isEditable ?? true,
      enabled: props.filter?.enabled ?? false,
      uuid: props.filter?.uuid ?? '',
      owner: props.filter?.owner ?? '',
      groups: data.selectedGroups.map((group) => group.value),
    };

    const defaultCallbacks = mutateCallbacks(
      toast,
      messages,
      props.action,
      'filter',
      setIsSubmitting,
      props.setShowDialog
    );

    const extendedCallbacks = {
      ...defaultCallbacks,
      onSuccess: () => {
        // First run the default onSuccess callback if it exists
        if (defaultCallbacks.onSuccess) {
          defaultCallbacks.onSuccess();
        }

        if (isFilterApplied(filterToSave, customFilters)) {
          const newFilters = customFilters.map((f) =>
            f.uuid === filterToSave.uuid
              ? { ...filterToSave, enabled: true }
              : f
          );
          setCustomFilters(newFilters);
        }
      },
    };

    if (props.action === 'edit') {
      const filterName = props.filter?.name
        ? props.filter?.name
        : 'unknown filter';
      const id = props.filter?.uuid;
      if (!id) {
        toast({
          className: 'save-filter-error-toast',
          title: 'Error!',
          description: messages.filtersEditFailed.replace(
            '{label}',
            filterName
          ),
          variant: 'destructive',
        });
        props.setShowDialog(false);
        return;
      }
    }
    props.mutate(filterToSave, extendedCallbacks);
  };

  const handleConfirm = React.useCallback(() => {
    props.setShowDialog(false);
  }, [props]);

  const { handleClose, ExitWarningDialog } = useDialogExitWarning({
    onConfirm: handleConfirm,
    title: messages.filtersWarningTitle || 'Discard filter changes?',
    description:
      messages.filtersWarningDescription ||
      'Your filter configuration will be lost. Are you sure you want to close?',
  });

  return (
    <Form {...form}>
      <DialogForm
        showDialog={props.showDialog}
        onOpenChange={props.setShowDialog}
        title={dialogTitle}
        description={dialogDescription}
        isLoading={isLoadingGroups}
        hasLoaded={!isLoadingGroups && !!groups}
        buttonId="filter-submit-button"
        buttonText={
          isSubmitting ? messages.genericSubmitting : messages.genericSubmit
        }
        buttonDisabled={isSubmitting}
        buttonOnClick={form.handleSubmit(onSubmit)}
        onClose={handleClose}
      >
        <SaveFilterForm
          form={form}
          isOwner={props.action === 'create' || isOwner(props.filter)}
        />
      </DialogForm>
      <ExitWarningDialog />
    </Form>
  );
}
